import { type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { type GetPlacementApplicationsResponse } from "../types/fetchPlacementApplications.schema";
import { PlacementApplicationStatus } from "../types/fetchPlacements.schema";
import {
  type FetchPlacementApplicationsParams,
  useFetchPlacementApplications,
} from "./useFetchPlacementApplications";

/**
 * Default page size for pending interview placement applications.
 * Larger than default to enable client-side counting without additional requests.
 */
const DEFAULT_PAGE_SIZE = 200;

interface UseGetPendingInterviewPlacementApplicationsParams extends Omit<
  FetchPlacementApplicationsParams,
  "filter"
> {
  additionalFilters?: FetchPlacementApplicationsParams["filter"];
}

/**
 * Fetch placement applications where the worker has a pending interview
 * (status = "interview:pending").
 *
 * These are applications where the interview has been booked but documents
 * are not yet submitted (submit documents later flow).
 *
 * Note: Uses a larger page size (200) to enable efficient client-side counting.
 * For badge counts, use data?.pages[0]?.data.length ?? 0 from the first page.
 */
export function useGetPendingInterviewPlacementApplications(
  params: UseGetPendingInterviewPlacementApplicationsParams,
  options: UseInfiniteQueryOptions<GetPlacementApplicationsResponse, AxiosError> = {}
) {
  const { workerId, additionalFilters, sort, pageSize } = params;

  return useFetchPlacementApplications(
    {
      workerId,
      sort,
      pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
      filter: {
        ...additionalFilters,
        status: PlacementApplicationStatus.INTERVIEW_PENDING,
      },
    },
    options
  );
}
